/**
 * Cuppy's mobile testimonial rail uses native horizontal scrolling for touch
 * swipes. These controls only keep the dot state in sync and provide direct
 * keyboard-accessible navigation to each story.
 */
class TuckySocialProofCarousel extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement|null} */
    this.rail = null;
    /** @type {HTMLElement[]} */
    this.stories = [];
    /** @type {HTMLElement[]} */
    this.dots = [];
    this.frame = 0;
    this.onScroll = () => {};
    /** @type {(event: MouseEvent) => void} */
    this.onDotClick = () => {};
  }

  connectedCallback() {
    this.rail = /** @type {HTMLElement|null} */ (this.querySelector('[data-social-proof-rail]'));
    this.stories = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-social-proof-story]')]);
    this.dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-social-proof-dot]')]);

    if (!this.rail || this.stories.length < 2 || this.stories.length !== this.dots.length) {
      return;
    }

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateActiveDot());
    };
    this.onDotClick = (event) => {
      const index = this.dots.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
      const story = this.stories[index];
      const firstStory = this.stories[0];
      if (!story || !firstStory || !this.rail) return;

      this.rail.scrollTo({
        left: story.offsetLeft - firstStory.offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      this.setActiveDot(index);
    };

    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.updateActiveDot();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.onScroll);
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    cancelAnimationFrame(this.frame);
  }

  updateActiveDot() {
    if (!this.rail || !this.stories?.length) return;

    const railLeft = this.rail.getBoundingClientRect().left;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    this.stories.forEach((story, index) => {
      const distance = Math.abs(story.getBoundingClientRect().left - railLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    this.setActiveDot(nearestIndex);
  }

  /** @param {number} index */
  setActiveDot(index) {
    this.dots?.forEach((dot, dotIndex) => {
      if (dotIndex === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('tucky-social-proof-carousel')) {
  customElements.define('tucky-social-proof-carousel', TuckySocialProofCarousel);
}
